import { format } from 'owa-localize';
import * as React from 'react';
import { getOwaResourceUrl } from 'owa-resource-url';
import RibbonButton, { RibbonButtonState } from './buttons/RibbonButton';
import type RibbonFormatState from '../store/schema/RibbonFormatState';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { IconButton } from '@fluentui/react/lib/Button';
import { Image } from '@fluentui/react/lib/Image';
import { observer } from 'mobx-react-lite';
import { convertHotkeyStringToReadableText } from 'owa-readable-hotkeys/lib/utils/convertHotkeyStringToReadableText';
import fixupHotkeyCommand from 'owa-hotkeys/lib/utils/fixupHotkeyCommand';
import { AriaRoles } from 'owa-accessibility';
import { ControlIcons } from 'owa-control-icons';
import type { EditorViewState } from 'owa-editor';

let styles = require('./Ribbon.scss');
let classNames = require('classnames');

export type RibbonButtonViewProps = {
    ribbonButton: RibbonButton;
    editorViewState: EditorViewState;
    formatViewState: RibbonFormatState;
    isDropDownOpen: boolean;
    containerRef: React.Ref<HTMLDivElement>;
    onExecute: () => void;
    onShowDropDown?: () => void;
    isRtl: boolean;
    isDarkMode: boolean;
    isRibbonDisabled: () => boolean;
};

const RibbonButtonView = (props: RibbonButtonViewProps): JSX.Element => {
    const {
        ribbonButton,
        formatViewState,
        editorViewState,
        isDropDownOpen,
        onExecute,
        onShowDropDown,
        containerRef,
        isRtl,
        isDarkMode,
        isRibbonDisabled,
    } = props;
    const {
        key,
        buttonState,
        title,
        shortcut,
        createDropDownCallback,
        imageRtlSrc,
        imageSrc,
        darkModeIconUseInvert,
        additionalRenderer,
    } = ribbonButton;
    const image = isRtl && imageRtlSrc ? imageRtlSrc : imageSrc;
    const buttonStateValue = buttonState ? buttonState(formatViewState) : RibbonButtonState.Normal;
    const isDisabled = buttonStateValue == RibbonButtonState.Disabled || isRibbonDisabled();
    const buttonClassName = classNames(
        styles.ribbonButton,
        image && styles.hoverableButton,
        (isDropDownOpen || buttonStateValue == RibbonButtonState.Checked) && styles.isChecked,
        isDisabled && styles.isDisabled
    );
    const titleStr = format(
        title(),
        shortcut == null
            ? ''
            : convertHotkeyStringToReadableText(
                  // cast back to string because fixupHotkeyCommand returns the same type it was passed
                  fixupHotkeyCommand(shortcut) as string
              )
    );
    const hasPopUp = createDropDownCallback != null;
    return (
        <div className={styles.ribbonButtonWrapper} ref={containerRef} key={key}>
            <TooltipHost content={titleStr} styles={{ root: { display: 'inline-block' } }}>
                {additionalRenderer?.(editorViewState, formatViewState)}
                <IconButton
                    className={buttonClassName}
                    data-is-focusable={!!image}
                    onClick={isDisabled ? null : onExecute || onShowDropDown}
                    ariaLabel={titleStr}
                    disabled={isDisabled}
                    checked={buttonState && buttonStateValue == RibbonButtonState.Checked}
                    aria-haspopup={hasPopUp}
                    role={AriaRoles.option}>
                    {image && (
                        <Image
                            className={classNames(
                                styles.image,
                                isDarkMode &&
                                    (darkModeIconUseInvert
                                        ? styles.invertIcons
                                        : styles.brightenIcons)
                            )}
                            shouldFadeIn={false}
                            alt={''}
                            src={getOwaResourceUrl(image)}
                        />
                    )}
                </IconButton>
                {createDropDownCallback && onExecute && (
                    <IconButton
                        className={styles.dropDownButton}
                        onClick={isDisabled ? null : onShowDropDown}
                        data-is-focusable={true}
                        ariaLabel={titleStr}
                        disabled={isDisabled}
                        aria-haspopup={hasPopUp}
                        role={AriaRoles.option}
                        iconProps={{ iconName: ControlIcons.ChevronDown }}></IconButton>
                )}
            </TooltipHost>
        </div>
    );
};

export default observer(RibbonButtonView);
